import {
  BackpressureIssue,
  DeliveryIssue,
  Issue,
  IssueStatus,
  IssueType,
  RequestIssue,
  TransformationIssue,
} from '../../../../typings/Issue.interface';
import { RequestRejectionCause } from '../../../../typings/Request.interface';
import { TeamRole } from '../../../../typings/TeamMember.interface';
import { IconName } from '../components/common/base/Icon';
import { IssuesRenderData } from '../components/scenes/DashboardScene/Issues/IssueList';
import LINKS from '../configs/links';
import { capitalizeFirstLetter, pluralize } from './';
import { getMsIntervalByUnit } from './rules';

type IssueTitlePart = { label: string; to?: string; type?: 'link' | 'label' };
export type IssueTitleParts = IssueTitlePart[];

const quoteIssueResource = (label?: string) => (label ? `"${label}"` : '');

const rejection_cause_labels: Record<RequestRejectionCause, string> = {
  SOURCE_DISABLED: 'Source Disabled',
  NO_CONNECTION: 'No Connection',
  VERIFICATION_FAILED: 'Verification Failed',
  UNSUPPORTED_HTTP_METHOD: 'Unsupported HTTP Method',
  UNSUPPORTED_CONTENT_TYPE: 'Unsupported Content Type',
  UNPARSABLE_JSON: 'Unparsable JSON',
  PAYLOAD_TOO_LARGE: 'Payload Too Large',
  INGESTION_FATAL: 'Ingestion Error',
  UNKNOWN: 'Unknown',
};

const formatRejectionCause = (cause: RequestRejectionCause): string => {
  return rejection_cause_labels[cause] || cause;
};

export const issue_type_configs: Record<
  IssueType,
  {
    icon: IconName;
    label: string;
    description?: string;
    docs?: {
      label: string;
      to: string;
    };
    getIssueTitleParts: (
      issue: Issue,
      render_data: IssuesRenderData,
      plain: boolean,
    ) => IssueTitleParts;
    getIssueDescrition: (issue: Issue, render_data: IssuesRenderData) => string;
  }
> = {
  delivery: {
    icon: 'send',
    label: 'Delivery',
    docs: {
      label: 'Issues related to the attempted delivery of requests.',
      to: LINKS.product_docs.issue_triggers_delivery,
    },
    description: 'Issues related to the attempted delivery of requests.',
    getIssueTitleParts: (
      issue: DeliveryIssue,
      render_data: Pick<IssuesRenderData, 'webhooks_by_id'>,
      plain: boolean,
    ) => {
      return [
        ...issue.aggregation_keys.response_status.map((status) => {
          const block = {
            label: `HTTP ${status} on connection`,
          } as IssueTitlePart;

          if (!plain) {
            block.to = `/issues/${issue.id}`;
            block.type = 'link' as const;
          }

          return block;
        }),
        ...issue.aggregation_keys.error_code.map((code) => {
          const block = {
            label: `Code ${code} on connection`,
          } as IssueTitlePart;

          if (!plain) {
            block.to = `/issues/${issue.id}`;
            block.type = 'link' as const;
          }

          return block;
        }),
        ...issue.aggregation_keys.webhook_id.map((id) => {
          const block = {
            label: render_data.webhooks_by_id?.[id]?.full_name || '',
          } as IssueTitlePart;

          if (plain) {
            block.label = quoteIssueResource(block.label);
          } else {
            block.to = `/connections/${id}`;
            block.type = 'label' as const;
          }

          return block;
        }),
      ];
    },
    getIssueDescrition: (issue: DeliveryIssue) => {
      const response_status_string = issue.aggregation_keys.response_status.join(', ');
      const error_code_string = issue.aggregation_keys.error_code.join(', ');
      let description = 'You are receiving an ';
      if (response_status_string) {
        description += `HTTP ${response_status_string} response`;
      }
      if (response_status_string && error_code_string) {
        description += ' and ';
      }
      if (error_code_string) {
        description += `CODE ${error_code_string}`;
      }
      description += ` on your connection${
        issue.aggregation_keys.webhook_id.length > 1 ? 's' : ''
      }.`;
      return description;
    },
  },
  transformation: {
    icon: 'transformation',
    label: 'Transformation',
    docs: {
      label: 'Issues that occur in the data manipulation step of a request.',
      to: LINKS.product_docs.issue_triggers_transformation,
    },
    description: 'Issues that occur in the data manipulation step of a request.',
    getIssueTitleParts: (
      issue: TransformationIssue,
      render_data: Pick<IssuesRenderData, 'transformations_by_id'>,
      plain: boolean,
    ) => {
      return [
        ...issue.aggregation_keys.log_level.map((level) => {
          const block = {
            label: `${capitalizeFirstLetter(level)} execution failure on transformation`,
          } as IssueTitlePart;

          if (!plain) {
            block.to = `/issues/${issue.id}`;
            block.type = 'link' as const;
          }

          return block;
        }),
        ...issue.aggregation_keys.transformation_id.map((id) => {
          const block = {
            label: render_data.transformations_by_id?.[id]?.name,
          } as IssueTitlePart;

          if (plain) {
            block.label = quoteIssueResource(block.label);
          } else {
            block.to = `/transformations/${id}`;
            block.type = 'label' as const;
          }

          return block;
        }),
      ];
    },
    getIssueDescrition: (
      issue: TransformationIssue,
      render_data: Pick<IssuesRenderData, 'transformations_by_id'>,
    ) => {
      const transformation =
        render_data.transformations_by_id?.[issue.aggregation_keys.transformation_id[0]];
      const fatal = issue.aggregation_keys.log_level.includes('fatal');
      const other_levels = issue.aggregation_keys.log_level.filter((l) => l !== 'fatal');
      let description = `Your transformation "${transformation?.name}" `;

      if (fatal) {
        if (other_levels.length > 0) {
          description =
            description +
            `failed to execute and logged ${other_levels.join(' and ')} while executing.`;
        } else {
          description = description + `failed to execute.`;
        }
      } else {
        description = description + `logged ${other_levels.join(' and ')} while executing.`;
      }
      description +=
        ' Update your transformation code and retry the impacted ignored events to fix this issue.';
      return description;
    },
  },
  backpressure: {
    icon: 'rate_limit',
    label: 'Backpressure',
    docs: {
      label:
        'Issues caused when the estimated event queue time exceeds your configured delay threshold.',
      to: LINKS.product_docs.issue_triggers_backpressure,
    },
    description:
      'Issues caused when the estimated event queue time (backpressure) exceeds your configured delay threshold.',
    getIssueTitleParts: (
      issue: BackpressureIssue,
      render_data: Pick<IssuesRenderData, 'destinations_by_id'>,
      plain: boolean,
    ) => {
      return [
        ...issue.aggregation_keys.delay.map((delay) => {
          const [value, unit] = getMsIntervalByUnit(delay, 'delay');
          const block = {
            label: `Backpressure of over ${value} ${pluralize(value, unit)} on destination`,
          } as IssueTitlePart;

          if (!plain) {
            block.to = `/issues/${issue.id}`;
            block.type = 'link' as const;
          }

          return block;
        }),
        ...issue.aggregation_keys.destination_id.map((id) => {
          const block = {
            label: render_data.destinations_by_id?.[id]?.name || '',
          } as IssueTitlePart;

          if (plain) {
            block.label = quoteIssueResource(block.label);
          } else {
            block.to = `/destinations/${id}`;
            block.type = 'label' as const;
          }

          return block;
        }),
      ];
    },
    getIssueDescrition: (
      issue: BackpressureIssue,
      render_data: Pick<IssuesRenderData, 'destinations_by_id'>,
    ) => {
      const delay = issue.aggregation_keys.delay[0];
      const destination =
        render_data.destinations_by_id?.[issue.aggregation_keys.destination_id[0]];
      const [value, unit] = getMsIntervalByUnit(delay, 'delay');
      const description = `Your destination "${destination?.name}" has a backlog of attempts that will take more than ${value} ${unit} to clear.`;
      return description;
    },
  },
  request: {
    icon: 'requests',
    label: 'Request',
    docs: {
      label: 'Issues related to rejected webhook requests.',
      to: LINKS.product_docs.issue_triggers_request,
    },
    description: 'Issues related to rejected webhook requests.',
    getIssueTitleParts: (
      issue: RequestIssue,
      render_data: Pick<IssuesRenderData, 'sources_by_id'>,
      plain: boolean,
    ) => {
      return [
        ...issue.aggregation_keys.rejection_cause.map((cause) => {
          const block = {
            label: `${formatRejectionCause(cause)} on source`,
          } as IssueTitlePart;

          if (!plain) {
            block.to = `/issues/${issue.id}`;
            block.type = 'link' as const;
          }

          return block;
        }),
        ...issue.aggregation_keys.source_id.map((id) => {
          const block = {
            label: render_data.sources_by_id?.[id]?.name || '',
          } as IssueTitlePart;

          if (plain) {
            block.label = quoteIssueResource(block.label);
          } else {
            block.to = `/sources/${id}`;
            block.type = 'label' as const;
          }

          return block;
        }),
      ];
    },
    getIssueDescrition: (
      issue: RequestIssue,
      render_data: Pick<IssuesRenderData, 'sources_by_id'>,
    ) => {
      const source = render_data.sources_by_id?.[issue.aggregation_keys.source_id[0]];
      const causes = issue.aggregation_keys.rejection_cause.map(formatRejectionCause).join(', ');
      const description = `Your source "${source?.name}" is rejecting requests due to ${causes}.`;
      return description;
    },
  },
};

export const getIssueDescrition = (issue: Issue, render_data: IssuesRenderData) => {
  const config = issue_type_configs[issue.type];
  if (!config) {
    return '';
  }
  return config.getIssueDescrition(issue, render_data);
};

export const getIssueTitleParts = (
  issue: Issue,
  render_data: IssuesRenderData,
  plain = false,
): IssueTitleParts => {
  const config = issue_type_configs[issue.type];
  if (!config) {
    return [{ label: capitalizeFirstLetter(issue.type) }];
  }
  return config.getIssueTitleParts(issue, render_data, plain);
};

export const ISSUE_STATUS_CONFIGS: Record<
  IssueStatus,
  {
    label: string;
    description: string;
    icon: IconName;
    badge_theme: 'primary' | 'muted' | 'danger' | 'warning' | 'success';
    default_action: keyof typeof issue_actions;
    current_action: keyof typeof issue_actions;
  }
> = {
  OPENED: {
    label: 'Open',
    description: 'Issues that are newly created and not yet acknowledged.',
    icon: 'error',
    badge_theme: 'danger',
    default_action: 'acknowledge',
    current_action: 'reopen',
  },
  ACKNOWLEDGED: {
    label: 'Acknowledged',
    description: 'Issues that are ongoing and acknowledged by a team member.',
    icon: 'awknowledged',
    badge_theme: 'warning',
    default_action: 'resolve',
    current_action: 'acknowledge',
  },
  RESOLVED: {
    label: 'Resolved',
    description:
      'Issues that have been marked as resolved. Will be re-open if the issue re-occurs.',
    icon: 'done_all',
    badge_theme: 'success',
    default_action: 'dismiss',
    current_action: 'resolve',
  },
  IGNORED: {
    label: 'Ignored',
    description: 'Issues that have been ignored. Will not be re-open if the issue re-occurs.',
    icon: 'notification_off',
    badge_theme: 'muted',
    default_action: 'reopen',
    current_action: 'ignore',
  },
} as const;

export const issue_actions = {
  acknowledge: {
    label: 'Acknowledge',
    danger: false,
    role: 'member' as TeamRole,
    target_status: 'ACKNOWLEDGED' as IssueStatus,
    icon: ISSUE_STATUS_CONFIGS['ACKNOWLEDGED'].icon,
  },
  resolve: {
    label: 'Resolve',
    danger: false,
    role: 'member' as TeamRole,
    target_status: 'RESOLVED' as IssueStatus,
    icon: ISSUE_STATUS_CONFIGS['RESOLVED'].icon,
  },
  ignore: {
    label: 'Ignore',
    danger: false,
    role: 'member' as TeamRole,
    target_status: 'IGNORED' as IssueStatus,
    icon: ISSUE_STATUS_CONFIGS['IGNORED'].icon,
  },
  reopen: {
    label: 'Re-open',
    danger: false,
    role: 'member' as TeamRole,
    target_status: 'OPENED' as IssueStatus,
    icon: ISSUE_STATUS_CONFIGS['OPENED'].icon,
  },
  dismiss: {
    label: 'Dismiss',
    danger: true,
    role: 'member' as TeamRole,
    target_status: 'IGNORED' as IssueStatus,
    icon: 'block' as IconName,
  },
};
